<template>
  <v-row>
    <v-col sm="12" md="12">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-file-chart</v-icon>
          Relatórios
        </v-card-title>

        <v-card-text>
          <v-form ref="reportForm" v-model="isFormValid" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="filters.bsId"
                  :items="beautySpots"
                  item-text="label"
                  item-value="id"
                  :loading="isLoadingBeautySpots"
                  :search-input.sync="beautySpotSearch"
                  :filter="() => true"
                  label="Beauty Spot"
                  placeholder="Selecione o Beauty Spot"
                  clearable
                  :rules="requiredRule"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="filters.report"
                  :items="reportOptions"
                  item-text="text"
                  item-value="value"
                  label="Relatório"
                  :rules="requiredRule"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="filters.periodPreset"
                  :items="periodPresetOptions"
                  item-text="text"
                  item-value="value"
                  label="Período"
                  :rules="requiredRule"
                  @change="applyPeriodPreset"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  :disabled="filters.periodPreset !== 'custom'"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDisplayDate(filters.startDate)"
                      label="Data inicial"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      :rules="requiredRule"
                      :disabled="filters.periodPreset !== 'custom'"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filters.startDate"
                    locale="pt-BR"
                    @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="4">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  :disabled="filters.periodPreset !== 'custom'"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDisplayDate(filters.endDate)"
                      label="Data final"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      :rules="requiredRule"
                      :disabled="filters.periodPreset !== 'custom'"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="filters.endDate"
                    locale="pt-BR"
                    @input="endDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="d-flex justify-end">
                <v-btn class="btn-save" @click="downloadCsvReport">
                  <v-icon left>mdi-download</v-icon>
                  Gerar CSV
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import { CHANGE_PROGRESS } from "@/store/mutations/mutations.type";
import { BeautySpotsService } from "@/common/api/beautyspot.service";
import { ReportsService } from "@/common/api/reports.service";

export default {
  name: "Reports",
  data: () => ({
    isFormValid: false,
    isLoadingBeautySpots: false,
    beautySpotSearch: "",
    beautySpotSearchTimeout: null,
    beautySpots: [],
    startDateMenu: false,
    endDateMenu: false,

    reportOptions: [
      {
        text: "Faturamento por BM mês a mês",
        value: "bmRevenueByMonth"
      }
    ],

    periodPresetOptions: [
      { text: "Este mês", value: "this_month" },
      { text: "Mês passado", value: "previous_month" },
      { text: "Últimos 30 dias", value: "last_30_days" },
      { text: "Semana passada (calendário)", value: "last_calendar_week" },
      { text: "Personalizado", value: "custom" }
    ],

    filters: {
      bsId: null,
      report: "bmRevenueByMonth",
      periodPreset: "this_month",
      startDate: "",
      endDate: ""
    },

    requiredRule: [v => !!v || "Campo obrigatório"]
  }),

  computed: {
    ...mapGetters(["snackbar"])
  },

  watch: {
    beautySpotSearch(value) {
      clearTimeout(this.beautySpotSearchTimeout);
      this.beautySpotSearchTimeout = setTimeout(() => {
        this.loadBeautySpots(value);
      }, 300);
    }
  },

  mounted() {
    this.applyPeriodPreset();
    this.loadBeautySpots();
  },

  beforeDestroy() {
    clearTimeout(this.beautySpotSearchTimeout);
  },

  methods: {
    formatDisplayDate(dateString) {
      if (!dateString) {
        return "";
      }

      const parts = dateString.split("-");
      if (parts.length !== 3) {
        return dateString;
      }

      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    },

    formatDate(date) {
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    addDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    },

    startOfWeek(date) {
      const baseDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const day = baseDate.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      return this.addDays(baseDate, diff);
    },

    applyPeriodPreset() {
      const today = new Date();
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      if (this.filters.periodPreset === "this_month") {
        const start = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
        this.filters.startDate = this.formatDate(start);
        this.filters.endDate = this.formatDate(todayDate);
        return;
      }

      if (this.filters.periodPreset === "previous_month") {
        const start = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
        const end = new Date(todayDate.getFullYear(), todayDate.getMonth(), 0);
        this.filters.startDate = this.formatDate(start);
        this.filters.endDate = this.formatDate(end);
        return;
      }

      if (this.filters.periodPreset === "last_30_days") {
        const start = this.addDays(todayDate, -29);
        this.filters.startDate = this.formatDate(start);
        this.filters.endDate = this.formatDate(todayDate);
        return;
      }

      if (this.filters.periodPreset === "last_calendar_week") {
        const currentWeekStart = this.startOfWeek(todayDate);
        const lastWeekStart = this.addDays(currentWeekStart, -7);
        const lastWeekEnd = this.addDays(currentWeekStart, -1);
        this.filters.startDate = this.formatDate(lastWeekStart);
        this.filters.endDate = this.formatDate(lastWeekEnd);
        return;
      }

      if (this.filters.periodPreset === "custom") {
        if (!this.filters.startDate || !this.filters.endDate) {
          this.filters.startDate = this.formatDate(todayDate);
          this.filters.endDate = this.formatDate(todayDate);
        }
      }
    },

    async loadBeautySpots(search = "") {
      this.isLoadingBeautySpots = true;
      try {
        const { data } = await BeautySpotsService.search(1, 100, true, search, "companyName", false);
        const items = Array.isArray(data && data.items) ? data.items : [];
        this.beautySpots = items.map(item => ({
          ...item,
          label: `${item.fantasyName || ""} - ${item.companyName || ""}`
        }));
      } catch (error) {
        this.snackbar.snackbar = true;
        this.snackbar.text = "Erro ao carregar Beauty Spots";
        this.snackbar.color = "error";
      } finally {
        this.isLoadingBeautySpots = false;
      }
    },

    async downloadCsvReport() {
      if (!this.$refs.reportForm.validate()) {
        return;
      }

      if (this.filters.startDate > this.filters.endDate) {
        this.snackbar.snackbar = true;
        this.snackbar.text = "Data inicial deve ser menor ou igual à data final";
        this.snackbar.color = "error";
        return;
      }

      this.$store.commit(CHANGE_PROGRESS, true);
      try {
        const response = await ReportsService.downloadBMRevenueCsv(
          this.filters.bsId,
          this.filters.startDate,
          this.filters.endDate
        );

        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "text/csv;charset=utf-8"
        });

        const disposition = response.headers["content-disposition"] || "";
        const match = disposition.match(/filename="?([^"]+)"?/i);
        const fallbackName = `relatorio_faturamento_bm_bs_${this.filters.bsId}_${this.filters.startDate}_${this.filters.endDate}.csv`;
        const fileName = match && match[1] ? match[1] : fallbackName;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.snackbar.snackbar = true;
        this.snackbar.text = "Relatório gerado com sucesso";
        this.snackbar.color = "success";
      } catch (error) {
        this.snackbar.snackbar = true;
        this.snackbar.text = "Não foi possível gerar o relatório";
        this.snackbar.color = "error";
      } finally {
        this.$store.commit(CHANGE_PROGRESS, false);
      }
    }
  }
};
</script>
